import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import LoadingPage from "../../../../shared/components/LoadingPage";
import { isDateRangeInvalid } from "../../../../shared/components/OrganizerDatePicker";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { formatCpf } from "../../../../shared/utils/cpf";
import { formatDateTimeLabel } from "../../../../shared/utils/dateTime";
import Toast from "../../../../shared/components/Toast";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import {
  AuthUser,
  CategoryChangeRequest,
  OrganizerRankingPointsTable,
  OrganizerRankingSettings,
  OrganizerVisualIdentityBackgroundMode,
  OrganizerVisualIdentityColors,
  OrganizerVisualIdentitySettings,
  UserSidePreference,
} from "../../../auth/shared/types/auth";
import {
  getPlayerSidePreference,
  getOrganizerRankingSettings,
  getOrganizerVisualIdentity,
  listMyCategoryChangeRequests,
  requestCategoryChange,
  resetOrganizerRankingSettings,
  saveOrganizerRankingSettings,
  saveOrganizerVisualIdentity,
  updatePlayerSidePreference,
} from "../../../auth/shared/services/authService";
import {
  DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
  hasLowOrganizerIdentityContrast,
  isValidHexColor,
  normalizeHexColor,
  organizerIdentityStyle,
} from "../../../../shared/utils/organizerVisualIdentity";
import {
  getChallengeByIdMaybeAuth,
  listMyChallenges,
} from "../../../challenges/services/challengeAdminService";
import {
  ChallengeDetail,
  ChallengeHistoryItem,
  ChallengeMatch,
} from "../../../challenges/types/challengeAdmin";
import { normalizePairId } from "../../../challenges/utils/challengePairs";
import {
  listMyEventRegistrations,
  listMyGames,
  cancelMyEventRegistration,
  deleteMyEventRegistration,
  type MyEventRegistrationItem,
  type MyGameItem,
} from "../../shared/services/registrationPaymentsService";
import {
  listMyMatches,
  reviewMatchParticipant,
  updateMatch,
} from "../../../matches/shared/services/matchesService";
import type {
  CreateOpenMatchPayload,
  OpenMatchItem,
  OpenMatchParticipantStatus,
} from "../../../matches/shared/types/matches";
import { parseOpenMatchDateTimeValue } from "../../../matches/shared/utils/openMatchPresentation";
import {
  listMyAchievements,
  type Achievement,
} from "../../shared/services/achievementsService";
import {
  getOrganizerStatistics,
  type OrganizerStatistics,
  type OrganizerStatisticsPeriod,
} from "../../shared/services/organizerStatisticsService";
import { getPublicPlayerProfile } from "../../../players/shared/services/publicPlayerProfileService";
import type { PublicPlayerProfile } from "../../../players/shared/types/publicPlayerProfile";
import {
  getNotificationPreferences,
  saveNotificationPreferences,
  type NotificationPreferences,
} from "../../shared/services/notificationsService";
import MatchCreatePanel from "../../../matches/public/components/MatchCreatePanel";
import { listClubs } from "../../../clubs/shared/services/clubsService";
import type { Club } from "../../../clubs/shared/types/club";
import ProfileShareModal from "../components/ProfileShareModal";
import ProfileSidebar from "../components/ProfileSidebar";
import ProfileAchievementsTab from "../components/tabs/ProfileAchievementsTab";
import ProfileCategoryRequestTab from "../components/tabs/ProfileCategoryRequestTab";
import ProfileEventsTab, {
  type EventStatusFilter,
  type EventTypeFilter,
} from "../components/tabs/ProfileEventsTab";
import ProfileMyGamesTab, {
  ProfileMyGamesContent,
} from "../components/tabs/ProfileMyGamesTab";
import ProfileNotificationPreferencesTab from "../components/tabs/ProfileNotificationPreferencesTab";
import ProfileOpenMatchesTab from "../components/tabs/ProfileOpenMatchesTab";
import ProfileOrganizerStatisticsTab, {
  organizerChartPeriodQuery,
  type OrganizerChartPeriod,
} from "../components/tabs/ProfileOrganizerStatisticsTab";
import ProfileOverviewTab from "../components/tabs/ProfileOverviewTab";
import ProfileOrganizerRankingTab from "../components/tabs/ProfileOrganizerRankingTab";
import ProfileRegistrationsTab, {
  type RegistrationViewItem,
  type RegistrationVisualStatus,
} from "../components/tabs/ProfileRegistrationsTab";
import ProfileSecurityTab from "../components/tabs/ProfileSecurityTab";
import ProfileVisualIdentityTab from "../components/tabs/ProfileVisualIdentityTab";
import {
  buildPublicPlayerProfileUrl,
  createProfileShareBlob,
  downloadBlob,
  getCurrentRankingPositionFromPublicProfile,
  type ProfileSharePreview,
  type ProfileShareStats,
} from "../utils/profileShareCard";
import "./UserProfilePage.css";

type UserProfileTab = "profile" | "security" | "challenges" | "registrations";
type ProfileOpenMatchGroupKey =
  | "created"
  | "confirmed"
  | "invites"
  | "pending"
  | "history"
  | "canceled";
export type UserProfileAccordionKey =
  | "achievements"
  | "categoryRequest"
  | "statistics"
  | "history"
  | "myGames"
  | "openMatches"
  | "registrations"
  | "notifications"
  | "organizerRanking"
  | "visualIdentity"
  | "security";

const USER_PROFILE_TAB_STORAGE_KEY = "tnp:user-profile:selected-tab";
const USER_PROFILE_ACCORDIONS_STORAGE_KEY =
  "tnp:user-profile:open-accordions";
const USER_PROFILE_TABS: UserProfileTab[] = ["profile"];
const USER_PROFILE_ACCORDION_KEYS: UserProfileAccordionKey[] = [
  "achievements",
  "categoryRequest",
  "statistics",
  "history",
  "myGames",
  "openMatches",
  "registrations",
  "notifications",
  "organizerRanking",
  "visualIdentity",
  "security",
];

const CLOSED_PROFILE_ACCORDIONS: Record<UserProfileAccordionKey, boolean> = {
  achievements: false,
  categoryRequest: false,
  statistics: false,
  history: false,
  myGames: false,
  openMatches: false,
  registrations: false,
  notifications: false,
  organizerRanking: false,
  visualIdentity: false,
  security: false,
};

const PROFILE_OPEN_MATCH_GROUP_KEYS: ProfileOpenMatchGroupKey[] = [
  "created",
  "confirmed",
  "invites",
  "pending",
  "history",
  "canceled",
];

const PROFILE_SECTION_ALIASES: Record<string, UserProfileAccordionKey> = {
  achievements: "achievements",
  achievement: "achievements",
  conquistas: "achievements",
  categoryrequest: "categoryRequest",
  "category-request": "categoryRequest",
  category_request: "categoryRequest",
  categoria: "categoryRequest",
  mygames: "myGames",
  "my-games": "myGames",
  my_games: "myGames",
  games: "myGames",
  jogos: "myGames",
  openmatches: "openMatches",
  "open-matches": "openMatches",
  open_matches: "openMatches",
  matches: "openMatches",
  partidas: "openMatches",
  registrations: "registrations",
  registration: "registrations",
  inscricoes: "registrations",
  "minhas-inscricoes": "registrations",
  notifications: "notifications",
  "notification-preferences": "notifications",
  notificationpreferences: "notifications",
  preferences: "notifications",
  history: "history",
  events: "history",
  historico: "history",
  statistics: "statistics",
  stats: "statistics",
  "organizer-statistics": "statistics",
  organizerstatistics: "statistics",
  "organizer-ranking": "organizerRanking",
  organizerranking: "organizerRanking",
  ranking: "organizerRanking",
  "visual-identity": "visualIdentity",
  visualidentity: "visualIdentity",
  identity: "visualIdentity",
  security: "security",
  seguranca: "security",
};

export function resolveProfileSectionAccordionKey(
  value: string | null | undefined,
): UserProfileAccordionKey | null {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return PROFILE_SECTION_ALIASES[key] || null;
}

function isUserProfileTab(value: string): value is UserProfileTab {
  return USER_PROFILE_TABS.includes(value as UserProfileTab);
}

function readStoredUserProfileTab(): UserProfileTab {
  if (typeof window === "undefined") return "profile";
  try {
    const storedTab = window.localStorage.getItem(USER_PROFILE_TAB_STORAGE_KEY);
    return storedTab && isUserProfileTab(storedTab) ? storedTab : "profile";
  } catch {
    return "profile";
  }
}

function readStoredProfileAccordions() {
  if (typeof window === "undefined") return { ...CLOSED_PROFILE_ACCORDIONS };
  try {
    const raw = window.localStorage.getItem(USER_PROFILE_ACCORDIONS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") {
      return { ...CLOSED_PROFILE_ACCORDIONS };
    }
    return USER_PROFILE_ACCORDION_KEYS.reduce(
      (acc, key) => ({
        ...acc,
        [key]: Boolean(
          (parsed as Partial<Record<UserProfileAccordionKey, boolean>>)[key],
        ),
      }),
      { ...CLOSED_PROFILE_ACCORDIONS },
    );
  } catch {
    return { ...CLOSED_PROFILE_ACCORDIONS };
  }
}

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  inApp: true,
  email: true,
  events: true,
  ranking: true,
  payments: true,
  achievements: true,
};


type ProfileEventAchievements = {
  titles: number;
  runnersUp: number;
};

type ProfileRankingRow = {
  id: string;
  cpf?: string;
  cpf1?: string;
  cpf2?: string;
  label: string;
  wins: number;
  losses: number;
  gamesWon: number;
  gamesLost: number;
  gamesBalance: number;
  points: number;
};


const ORGANIZER_RANKING_FIELDS: Array<{
  modality: keyof OrganizerRankingPointsTable;
  icon: string;
  fields: Array<{
    key: string;
    labelKey: string;
  }>;
}> = [
  {
    modality: "super8",
    icon: "groups_3",
    fields: [
      { key: "first", labelKey: "first" },
      { key: "second", labelKey: "second" },
      { key: "third", labelKey: "third" },
      { key: "fourth", labelKey: "fourth" },
      { key: "fifth", labelKey: "fifth" },
      { key: "sixth", labelKey: "sixth" },
      { key: "seventh", labelKey: "seventh" },
      { key: "eighth", labelKey: "eighth" },
    ],
  },
  {
    modality: "challenge",
    icon: "emoji_events",
    fields: [
      { key: "first", labelKey: "first" },
      { key: "second", labelKey: "second" },
      { key: "third", labelKey: "third" },
      { key: "participation", labelKey: "participation" },
    ],
  },
  {
    modality: "poco",
    icon: "pool",
    fields: [
      { key: "first", labelKey: "first" },
      { key: "second", labelKey: "second" },
      { key: "third", labelKey: "third" },
      { key: "participation", labelKey: "participation" },
    ],
  },
  {
    modality: "tournament",
    icon: "military_tech",
    fields: [
      { key: "champion", labelKey: "champion" },
      { key: "runnerUp", labelKey: "runnerUp" },
      { key: "semifinal", labelKey: "semifinal" },
      { key: "quarterfinal", labelKey: "quarterfinal" },
      { key: "participation", labelKey: "participation" },
    ],
  },
];

function createEmptyOrganizerRankingTable(): OrganizerRankingPointsTable {
  return {
    super8: {
      first: "",
      second: "",
      third: "",
      fourth: "",
      fifth: "",
      sixth: "",
      seventh: "",
      eighth: "",
    },
    challenge: {
      first: "",
      second: "",
      third: "",
      participation: "",
    },
    poco: {
      first: "",
      second: "",
      third: "",
      participation: "",
    },
    tournament: {
      champion: "",
      runnerUp: "",
      semifinal: "",
      quarterfinal: "",
      participation: "",
    },
  };
}

function cloneOrganizerRankingTable(
  table?: OrganizerRankingPointsTable | null,
): OrganizerRankingPointsTable | null {
  if (!table) return null;
  return {
    super8: { ...table.super8 },
    challenge: { ...table.challenge },
    poco: { ...table.poco },
    tournament: { ...table.tournament },
  };
}

function hasOrganizerRankingPoint(table?: OrganizerRankingPointsTable | null) {
  if (!table) return false;
  return ORGANIZER_RANKING_FIELDS.some((group) =>
    group.fields.some((field) => {
      const value =
        table[group.modality][
          field.key as keyof (typeof table)[typeof group.modality]
        ];
      return value !== "" && value !== null && value !== undefined;
    }),
  );
}

function localizedCategoryLabel(value: AuthUser["category"] | string) {
  if (value === "7a") return siteTexts.common.category.c7a;
  if (value === "6a") return siteTexts.common.category.c6a;
  if (value === "5a") return siteTexts.common.category.c5a;
  if (value === "4a") return siteTexts.common.category.c4a;
  if (value === "3a") return siteTexts.common.category.c3a;
  if (value === "2a") return siteTexts.common.category.c2a;
  if (value === "open") return siteTexts.common.category.open;
  return siteTexts.common.category.notInformed;
}

function openMatchTime(match: Pick<OpenMatchItem, "startAt" | "createdAt">) {
  const startTime = parseOpenMatchDateTimeValue(match.startAt)?.getTime();
  if (Number.isFinite(startTime)) return startTime;
  const createdTime = new Date(String(match.createdAt || "")).getTime();
  return Number.isFinite(createdTime) ? createdTime : 0;
}

function isHistoricalOpenMatch(match: OpenMatchItem, nowTime = Date.now()) {
  if (match.status === "closed" || match.status === "completed") return true;
  const endTime = parseOpenMatchDateTimeValue(match.endAt)?.getTime();
  return Number.isFinite(endTime) && endTime < nowTime;
}

function sortProfileOpenMatches(matches: OpenMatchItem[]) {
  return [...matches].sort(
    (left, right) => openMatchTime(left) - openMatchTime(right),
  );
}

function formatMemberSinceLabel(user?: AuthUser | null) {
  const raw = String(
    user?.createdAt ||
      user?.created_at ||
      user?.createdDate ||
      user?.registrationDate ||
      user?.memberSince ||
      "",
  ).trim();
  if (!raw) return "-";
  const formatted = formatDateTimeLabel(raw);
  if (!formatted || formatted === "-") return "-";
  return formatted.split(" - ")[0] || formatted;
}

function normalizeProfileCpf(value?: string | null) {
  return String(value || "").replace(/\D/g, "");
}

function parseProfileScore(score: number | "" | undefined) {
  const value = Number(score);
  return Number.isFinite(value) ? value : null;
}

function getProfileFinishedMatches(
  detail: ChallengeDetail,
): Array<ChallengeMatch & { dupla1Label: string; dupla2Label: string }> {
  return (detail.roundsDetailed || []).flat().filter((match) => {
    const score1 = parseProfileScore(match.score1);
    const score2 = parseProfileScore(match.score2);
    return score1 !== null && score2 !== null && score1 !== score2;
  });
}

function calculateProfilePairRanking(detail: ChallengeDetail) {
  const matches = getProfileFinishedMatches(detail);
  const rows = (detail.pairs || []).map<ProfileRankingRow>((pair) => {
    const pairId = normalizePairId(pair.id);
    let gamesWon = 0;
    let gamesLost = 0;
    let wins = 0;
    let losses = 0;

    matches.forEach((match) => {
      const score1 = parseProfileScore(match.score1);
      const score2 = parseProfileScore(match.score2);
      if (score1 === null || score2 === null) return;
      if (normalizePairId(match.dupla1Id) === pairId) {
        gamesWon += score1;
        gamesLost += score2;
        if (score1 > score2) wins += 1;
        if (score1 < score2) losses += 1;
      }
      if (normalizePairId(match.dupla2Id) === pairId) {
        gamesWon += score2;
        gamesLost += score1;
        if (score2 > score1) wins += 1;
        if (score2 < score1) losses += 1;
      }
    });

    return {
      id: pairId || pair.id,
      cpf1: normalizeProfileCpf(pair.cpf1),
      cpf2: normalizeProfileCpf(pair.cpf2),
      label: pair.label,
      wins,
      losses,
      gamesWon,
      gamesLost,
      gamesBalance: gamesWon - gamesLost,
      points: 0,
    };
  });

  return rows.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.gamesBalance !== a.gamesBalance)
      return b.gamesBalance - a.gamesBalance;
    if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
    if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
    return a.label.localeCompare(b.label, "pt-BR");
  });
}

function calculateProfileSuper8Ranking(detail: ChallengeDetail) {
  const matches = getProfileFinishedMatches(detail);
  const pairById = (detail.pairs || []).reduce<
    Record<string, ChallengeDetail["pairs"][number]>
  >((acc, pair) => {
    const pairId = normalizePairId(pair.id);
    if (pairId) acc[pairId] = pair;
    return acc;
  }, {});
  const nameByCpf = (detail.participants || []).reduce<Record<string, string>>(
    (acc, item) => {
      const cpf = normalizeProfileCpf(item.cpf);
      if (cpf) acc[cpf] = item.name || "Jogador";
      return acc;
    },
    {},
  );
  const rows = new Map<string, ProfileRankingRow>();
  const ensureRow = (cpf: string) => {
    const safeCpf = normalizeProfileCpf(cpf);
    if (!safeCpf) return null;
    const existing = rows.get(safeCpf);
    if (existing) return existing;
    const created: ProfileRankingRow = {
      id: safeCpf,
      cpf: safeCpf,
      label: nameByCpf[safeCpf] || "Jogador",
      wins: 0,
      losses: 0,
      gamesWon: 0,
      gamesLost: 0,
      gamesBalance: 0,
      points: 0,
    };
    rows.set(safeCpf, created);
    return created;
  };

  (detail.participants || []).forEach((item) => ensureRow(item.cpf));
  matches.forEach((match) => {
    const score1 = parseProfileScore(match.score1);
    const score2 = parseProfileScore(match.score2);
    if (score1 === null || score2 === null) return;
    const pair1 = pairById[normalizePairId(match.dupla1Id)];
    const pair2 = pairById[normalizePairId(match.dupla2Id)];
    if (!pair1 || !pair2) return;
    [pair1.cpf1, pair1.cpf2].forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.gamesWon += score1;
      row.gamesLost += score2;
      if (score1 > score2) row.wins += 1;
      else row.losses += 1;
    });
    [pair2.cpf1, pair2.cpf2].forEach((cpf) => {
      const row = ensureRow(cpf);
      if (!row) return;
      row.gamesWon += score2;
      row.gamesLost += score1;
      if (score2 > score1) row.wins += 1;
      else row.losses += 1;
    });
  });

  return Array.from(rows.values())
    .map((row) => ({ ...row, gamesBalance: row.gamesWon - row.gamesLost }))
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
      if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
      return a.label.localeCompare(b.label, "pt-BR");
    });
}

function calculateProfilePocoRanking(detail: ChallengeDetail) {
  const rows = calculateProfilePairRanking(detail);
  const finalRound = detail.roundsDetailed?.[detail.roundsDetailed.length - 1];
  const finalMatch = Array.isArray(finalRound) ? finalRound[0] : null;
  if (!finalMatch) return rows;
  const score1 = parseProfileScore(finalMatch.score1);
  const score2 = parseProfileScore(finalMatch.score2);
  if (score1 === null || score2 === null || score1 === score2) return rows;
  const winnerId = normalizePairId(
    score1 > score2 ? finalMatch.dupla1Id : finalMatch.dupla2Id,
  );
  const loserId = normalizePairId(
    score1 > score2 ? finalMatch.dupla2Id : finalMatch.dupla1Id,
  );
  return [...rows].sort((a, b) => {
    const priorityA = a.id === winnerId ? 0 : a.id === loserId ? 1 : 2;
    const priorityB = b.id === winnerId ? 0 : b.id === loserId ? 1 : 2;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return 0;
  });
}

function calculateProfileEventPosition(
  detail: ChallengeDetail,
  userCpf: string,
) {
  const safeCpf = normalizeProfileCpf(userCpf);
  if (!safeCpf || detail.status !== "finished") return null;
  const ranking =
    detail.challengeType === "super8"
      ? calculateProfileSuper8Ranking(detail)
      : detail.challengeType === "poco"
        ? calculateProfilePocoRanking(detail)
        : calculateProfilePairRanking(detail);

  const index = ranking.findIndex((row) => {
    if (row.cpf && row.cpf === safeCpf) return true;
    return row.cpf1 === safeCpf || row.cpf2 === safeCpf;
  });
  return index >= 0 ? index + 1 : null;
}

function normalizeRegistrationVisualStatus(
  item: MyEventRegistrationItem,
): Exclude<RegistrationVisualStatus, "all"> {
  const registrationStatus = String(item.status || "")
    .trim()
    .toLowerCase();
  const paymentStatus = String(item.paymentStatus || "")
    .trim()
    .toLowerCase();
  const canceledByPayment =
    paymentStatus === "canceled" || paymentStatus === "refunded";
  if (registrationStatus === "pending" && canceledByPayment) return "overdue";
  if (registrationStatus === "canceled" || canceledByPayment) return "canceled";
  if (registrationStatus === "waitlist") return "waitlist";
  if (paymentStatus === "confirmed" || registrationStatus === "confirmed") {
    return "confirmed";
  }
  return "pending";
}

function buildRegistrationVisualMetadata(
  item: MyEventRegistrationItem,
): Pick<
  RegistrationViewItem,
  "visualStatusLabel" | "visualStatusHint" | "playerModeLabel"
> {
  const registrationType = String(item.registrationType || "")
    .trim()
    .toLowerCase();
  const partnerName = String(item.teammateName || "").trim();
  const playerModeLabel =
    registrationType === "fixed_pair"
      ? siteTexts.profile.registrations.mode.fixedPair
      : siteTexts.profile.registrations.mode.individual;

  const visualStatus = normalizeRegistrationVisualStatus(item);
  if (visualStatus === "confirmed") {
    return {
      visualStatusLabel: siteTexts.profile.registrations.statuses.confirmed,
      visualStatusHint: item.paymentConfirmedAt
        ? `${siteTexts.profile.registrations.hints.confirmedAtPrefix} ${formatDateTimeLabel(item.paymentConfirmedAt)}`
        : siteTexts.profile.registrations.hints.confirmedDefault,
      playerModeLabel,
    };
  }
  if (visualStatus === "waitlist") {
    return {
      visualStatusLabel: siteTexts.profile.registrations.statuses.waitlist,
      visualStatusHint:
        String(item.paymentStatus || "")
          .trim()
          .toLowerCase() === "confirmed"
          ? siteTexts.profile.registrations.hints.waitlistPaid
          : siteTexts.profile.registrations.hints.waitlistDefault,
      playerModeLabel,
    };
  }
  if (visualStatus === "overdue") {
    return {
      visualStatusLabel: siteTexts.profile.registrations.statuses.overdue,
      visualStatusHint: item.paymentCanceledAt
        ? `${siteTexts.profile.registrations.hints.overdueAtPrefix} ${formatDateTimeLabel(item.paymentCanceledAt)}`
        : siteTexts.profile.registrations.hints.overdueDefault,
      playerModeLabel,
    };
  }
  if (visualStatus === "canceled") {
    return {
      visualStatusLabel: siteTexts.profile.registrations.statuses.canceled,
      visualStatusHint: item.paymentCanceledAt
        ? `${siteTexts.profile.registrations.hints.canceledAtPrefix} ${formatDateTimeLabel(item.paymentCanceledAt)}`
        : siteTexts.profile.registrations.hints.canceledDefault,
      playerModeLabel,
    };
  }
  if (registrationType === "fixed_pair" && partnerName) {
    return {
      visualStatusLabel: siteTexts.profile.registrations.statuses.pending,
      visualStatusHint: `${siteTexts.profile.registrations.hints.pendingPairPrefix} (${partnerName})`,
      playerModeLabel,
    };
  }
  return {
    visualStatusLabel: siteTexts.profile.registrations.statuses.pending,
    visualStatusHint: siteTexts.profile.registrations.hints.pendingDefault,
    playerModeLabel,
  };
}

export default function UserProfilePage() {
  const {
    user,
    loading,
    updateProfile,
    changePassword,
    requestEmailConfirmationLink,
    token,
  } = useAuth();
  const dashboardTexts = siteTexts.profile.user.dashboard;
  const info = siteTexts.common.info;
  const organizerRankingModeDescriptions = {
    tnp_standard:
      siteTexts.profile.user.organizerRanking.modeDescriptions.tnp_standard,
    custom: siteTexts.profile.user.organizerRanking.modeDescriptions.custom,
  };
  const visualIdentityModeDescriptions = {
    tnp_standard:
      siteTexts.profile.user.visualIdentity.modeDescriptions.tnp_standard,
    custom:
      siteTexts.profile.user.visualIdentity.modeDescriptions.custom,
  };
  const [tab, setTab] = useState<UserProfileTab>(readStoredUserProfileTab);
  const [openProfileAccordions, setOpenProfileAccordions] = useState<
    Record<UserProfileAccordionKey, boolean>
  >(readStoredProfileAccordions);
  const [mobileProfileInfoOpen, setMobileProfileInfoOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [securityEmailDraft, setSecurityEmailDraft] = useState("");
  const [cpfDraft, setCpfDraft] = useState("");
  const [phoneDraft, setPhoneDraft] = useState("");
  const [sexDraft, setSexDraft] = useState<AuthUser["sex"]>("");
  const [categoryDraft, setCategoryDraft] = useState<AuthUser["category"]>("");
  const [clubIdDraft, setClubIdDraft] = useState("");
  const [sidePreferenceDraft, setSidePreferenceDraft] =
    useState<UserSidePreference>("ambos");
  const [savedSidePreference, setSavedSidePreference] =
    useState<UserSidePreference>("ambos");
  const [sidePreferenceLoading, setSidePreferenceLoading] = useState(false);
  const [avatarDraft, setAvatarDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailLinkLoading, setEmailLinkLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [currentPasswordDraft, setCurrentPasswordDraft] = useState("");
  const [newPasswordDraft, setNewPasswordDraft] = useState("");
  const [newPasswordConfirmDraft, setNewPasswordConfirmDraft] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [challenges, setChallenges] = useState<ChallengeHistoryItem[]>([]);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [eventAchievements, setEventAchievements] =
    useState<ProfileEventAchievements>({
      titles: 0,
      runnersUp: 0,
    });
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementsError, setAchievementsError] = useState("");
  const [notificationPreferences, setNotificationPreferences] =
    useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const [notificationPreferencesLoading, setNotificationPreferencesLoading] =
    useState(false);
  const [notificationPreferencesSaving, setNotificationPreferencesSaving] =
    useState(false);
  const [notificationPreferencesError, setNotificationPreferencesError] =
    useState("");
  const [rankingPosition, setRankingPosition] = useState<number | null>(null);
  const [nameFilter, setNameFilter] = useState("");
  const [dateFrom] = useState("");
  const [dateTo] = useState("");
  const [eventStatusFilter, setEventStatusFilter] =
    useState<EventStatusFilter>("all");
  const [eventTypeFilter, setEventTypeFilter] =
    useState<EventTypeFilter>("all");
  const [eventsPage, setEventsPage] = useState(1);
  const [myGamesUpcoming, setMyGamesUpcoming] = useState<MyGameItem[]>([]);
  const [myGamesHistory, setMyGamesHistory] = useState<MyGameItem[]>([]);
  const [myGamesLoading, setMyGamesLoading] = useState(false);
  const [myGamesError, setMyGamesError] = useState("");
  const [myOpenMatches, setMyOpenMatches] = useState<OpenMatchItem[]>([]);
  const [myOpenMatchesLoading, setMyOpenMatchesLoading] = useState(false);
  const [myOpenMatchesError, setMyOpenMatchesError] = useState("");
  const [openMatchParticipantActionId, setOpenMatchParticipantActionId] =
    useState<number | null>(null);
  const [editingOpenMatch, setEditingOpenMatch] =
    useState<OpenMatchItem | null>(null);
  const [openMatchClubs, setOpenMatchClubs] = useState<Club[]>([]);
  const [openMatchClubsLoading, setOpenMatchClubsLoading] = useState(false);
  const [profileClubs, setProfileClubs] = useState<Club[]>([]);
  const [profileClubsLoading, setProfileClubsLoading] = useState(false);
  const [showCategoryRequestForm, setShowCategoryRequestForm] = useState(false);
  const [requestedCategoryDraft, setRequestedCategoryDraft] =
    useState<AuthUser["category"]>("");
  const [categoryRequestReasonDraft, setCategoryRequestReasonDraft] =
    useState("");
  const [categoryRequestObservationDraft, setCategoryRequestObservationDraft] =
    useState("");
  const [categoryRequestLoading, setCategoryRequestLoading] = useState(false);
  const [categoryRequestSaving, setCategoryRequestSaving] = useState(false);
  const [categoryRequestError, setCategoryRequestError] = useState("");
  const [categoryRequestSuccess, setCategoryRequestSuccess] = useState("");
  const [categoryChangeRequests, setCategoryChangeRequests] = useState<
    CategoryChangeRequest[]
  >([]);
  const [pendingCategoryRequest, setPendingCategoryRequest] =
    useState<CategoryChangeRequest | null>(null);
  const [organizerRankingSettings, setOrganizerRankingSettings] =
    useState<OrganizerRankingSettings | null>(null);
  const [organizerRankingDraft, setOrganizerRankingDraft] =
    useState<OrganizerRankingPointsTable | null>(null);
  const [organizerRankingModeDraft, setOrganizerRankingModeDraft] = useState<
    "tnp_standard" | "custom"
  >("tnp_standard");
  const [organizerRankingLoading, setOrganizerRankingLoading] = useState(false);
  const [organizerRankingSaving, setOrganizerRankingSaving] = useState(false);
  const [organizerRankingError, setOrganizerRankingError] = useState("");
  const [organizerRankingSuccess, setOrganizerRankingSuccess] = useState("");
  const [visualIdentitySettings, setVisualIdentitySettings] =
    useState<OrganizerVisualIdentitySettings | null>(null);
  const [visualIdentityModeDraft, setVisualIdentityModeDraft] = useState<
    "tnp_standard" | "custom"
  >("tnp_standard");
  const [visualIdentityColorsDraft, setVisualIdentityColorsDraft] =
    useState<OrganizerVisualIdentityColors>(DEFAULT_TNP_VISUAL_IDENTITY_COLORS);
  const [visualIdentityBackgroundDraft, setVisualIdentityBackgroundDraft] =
    useState<OrganizerVisualIdentityBackgroundMode>("blue");
  const [visualIdentityPublicNameDraft, setVisualIdentityPublicNameDraft] =
    useState("");
  const [visualIdentityLogoDraft, setVisualIdentityLogoDraft] = useState("");
  const [visualIdentityLoading, setVisualIdentityLoading] = useState(false);
  const [visualIdentitySaving, setVisualIdentitySaving] = useState(false);
  const [visualIdentityError, setVisualIdentityError] = useState("");
  const [organizerStats, setOrganizerStats] =
    useState<OrganizerStatistics | null>(null);
  const [organizerStatsLoading, setOrganizerStatsLoading] = useState(false);
  const [organizerStatsError, setOrganizerStatsError] = useState("");
  const [organizerStatsPeriod, setOrganizerStatsPeriod] =
    useState<OrganizerStatisticsPeriod>("all");
  const [organizerStatsDateFrom, setOrganizerStatsDateFrom] = useState("");
  const [organizerStatsDateTo, setOrganizerStatsDateTo] = useState("");
  const [organizerChartPeriod, setOrganizerChartPeriod] =
    useState<OrganizerChartPeriod>("last_6_months");
  const [organizerChartStats, setOrganizerChartStats] =
    useState<OrganizerStatistics | null>(null);
  const [organizerChartStatsLoading, setOrganizerChartStatsLoading] =
    useState(false);
  const [organizerChartStatsError, setOrganizerChartStatsError] = useState("");
  const [myRegistrations, setMyRegistrations] = useState<
    MyEventRegistrationItem[]
  >([]);
  const [myRegistrationsLoading, setMyRegistrationsLoading] = useState(false);
  const [registrationStatusFilter, setRegistrationStatusFilter] =
    useState<RegistrationVisualStatus>("all");
  const [registrationActionId, setRegistrationActionId] = useState<
    number | null
  >(null);
  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "error";
  } | null>(null);
  const [profileShareOpen, setProfileShareOpen] = useState(false);
  const [myGamesModalTournamentId, setMyGamesModalTournamentId] = useState<
    number | null
  >(null);
  useBodyScrollLock(profileShareOpen);
  useBodyScrollLock(Boolean(myGamesModalTournamentId));
  const [profileSharePreview, setProfileSharePreview] =
    useState<ProfileSharePreview | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(USER_PROFILE_TAB_STORAGE_KEY, tab);
    } catch {
      // Local persistence is optional.
    }
  }, [tab]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        USER_PROFILE_ACCORDIONS_STORAGE_KEY,
        JSON.stringify(openProfileAccordions),
      );
    } catch {
      // Local persistence is optional.
    }
  }, [openProfileAccordions]);

  useEffect(() => {
    if (!user) return;
    setOpenProfileAccordions((current) => {
      const next = { ...current };
      let changed = false;
      const closeIfUnavailable = (
        key: UserProfileAccordionKey,
        available: boolean,
      ) => {
        if (!available && next[key]) {
          next[key] = false;
          changed = true;
        }
      };

      closeIfUnavailable(
        "statistics",
        user.role === "organizer" || user.role === "admin",
      );
      closeIfUnavailable("organizerRanking", user.role === "organizer");
      closeIfUnavailable("visualIdentity", user.role === "organizer");

      return changed ? next : current;
    });
  }, [user]);
  const [profileShareLoading, setProfileShareLoading] = useState(false);
  const [profileShareStatus, setProfileShareStatus] = useState("");
  const [profileSharePublicProfile, setProfileSharePublicProfile] =
    useState<PublicPlayerProfile | null>(null);
  const organizerLogoInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!user) return;
    setNameDraft(user.name || "");
    setSecurityEmailDraft(user.email || "");
    setCpfDraft(formatCpf(user.cpf || ""));
    setPhoneDraft(user.phone || "");
    setSexDraft(user.sex || "");
    setCategoryDraft(user.category || "");
    setClubIdDraft(user.clubId ? String(user.clubId) : "");
    setSidePreferenceDraft("ambos");
    setSavedSidePreference("ambos");
    setAvatarDraft(user.avatarUrl || "");
    setProfileSharePublicProfile(null);
  }, [user]);

  useEffect(() => {
    let active = true;
    if (!user) return undefined;
    setProfileClubsLoading(true);
    listClubs({})
      .then((response) => {
        if (!active) return;
        setProfileClubs(response.clubs || []);
      })
      .catch(() => {
        if (!active) return;
        setProfileClubs([]);
      })
      .finally(() => {
        if (!active) return;
        setProfileClubsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    let active = true;
    if (!token || !user?.cpf) return;
    setSidePreferenceLoading(true);
    getPlayerSidePreference(token, user.cpf)
      .then((sidePreference) => {
        if (!active) return;
        const nextSidePreference = sidePreference || "ambos";
        setSidePreferenceDraft(nextSidePreference);
        setSavedSidePreference(nextSidePreference);
      })
      .catch(() => {
        if (!active) return;
        setSidePreferenceDraft("ambos");
        setSavedSidePreference("ambos");
      })
      .finally(() => {
        if (!active) return;
        setSidePreferenceLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user?.cpf]);

  useEffect(() => {
    let active = true;
    if (!token) {
      setAchievements([]);
      setAchievementsError("");
      return;
    }
    setAchievementsLoading(true);
    setAchievementsError("");
    listMyAchievements(token)
      .then((items) => {
        if (!active) return;
        setAchievements(items);
      })
      .catch((err) => {
        if (!active) return;
        setAchievements([]);
        setAchievementsError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.achievements.loadError,
        );
      })
      .finally(() => {
        if (!active) return;
        setAchievementsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;
    if (!token) {
      setNotificationPreferences(DEFAULT_NOTIFICATION_PREFERENCES);
      setNotificationPreferencesError("");
      return;
    }
    setNotificationPreferencesLoading(true);
    setNotificationPreferencesError("");
    getNotificationPreferences(token)
      .then((preferences) => {
        if (!active) return;
        setNotificationPreferences(preferences);
      })
      .catch((err) => {
        if (!active) return;
        setNotificationPreferencesError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.notificationPreferences.loadError,
        );
      })
      .finally(() => {
        if (!active) return;
        setNotificationPreferencesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;
    if (!token || user?.role !== "organizer") {
      setOrganizerRankingSettings(null);
      setOrganizerRankingDraft(null);
      setOrganizerRankingModeDraft("tnp_standard");
      return;
    }
    setOrganizerRankingLoading(true);
    setOrganizerRankingError("");
    getOrganizerRankingSettings(token)
      .then((settings) => {
        if (!active) return;
        setOrganizerRankingSettings(settings);
        setOrganizerRankingDraft(settings.points);
        setOrganizerRankingModeDraft(
          settings.mode === "custom" ? "custom" : "tnp_standard",
        );
      })
      .catch((err) => {
        if (!active) return;
        setOrganizerRankingError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.organizerRanking.loadError,
        );
      })
      .finally(() => {
        if (active) setOrganizerRankingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user?.role]);

  useEffect(() => {
    let active = true;
    if (!token || (user?.role !== "organizer" && user?.role !== "admin")) {
      setOrganizerStats(null);
      setOrganizerStatsError("");
      return;
    }
    if (organizerStatsPeriod === "custom") {
      const hasBothDates = Boolean(
        organizerStatsDateFrom && organizerStatsDateTo,
      );
      if (!hasBothDates) {
        setOrganizerStatsError("");
        setOrganizerStatsLoading(false);
        return;
      }
      if (isDateRangeInvalid(organizerStatsDateFrom, organizerStatsDateTo)) {
        setOrganizerStatsError(
          siteTexts.profile.user.organizerStatistics.invalidDateRange,
        );
        setOrganizerStatsLoading(false);
        return;
      }
    }
    setOrganizerStatsLoading(true);
    setOrganizerStatsError("");
    const timeoutId = window.setTimeout(
      () => {
        getOrganizerStatistics(token, {
          period: organizerStatsPeriod,
          dateFrom:
            organizerStatsPeriod === "custom" ? organizerStatsDateFrom : "",
          dateTo: organizerStatsPeriod === "custom" ? organizerStatsDateTo : "",
        })
          .then((stats) => {
            if (!active) return;
            setOrganizerStats(stats);
          })
          .catch((err) => {
            if (!active) return;
            setOrganizerStats(null);
            setOrganizerStatsError(
              err instanceof Error
                ? err.message
                : siteTexts.profile.user.organizerStatistics.loadError,
            );
          })
          .finally(() => {
            if (!active) return;
            setOrganizerStatsLoading(false);
          });
      },
      organizerStatsPeriod === "custom" ? 300 : 0,
    );
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    organizerStatsDateFrom,
    organizerStatsDateTo,
    organizerStatsPeriod,
    token,
    user?.role,
  ]);

  useEffect(() => {
    let active = true;
    if (!token || (user?.role !== "organizer" && user?.role !== "admin")) {
      setOrganizerChartStats(null);
      setOrganizerChartStatsError("");
      return;
    }
    setOrganizerChartStatsLoading(true);
    setOrganizerChartStatsError("");
    getOrganizerStatistics(
      token,
      organizerChartPeriodQuery(organizerChartPeriod),
    )
      .then((stats) => {
        if (!active) return;
        setOrganizerChartStats(stats);
      })
      .catch((err) => {
        if (!active) return;
        setOrganizerChartStatsError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.organizerStatistics.loadError,
        );
      })
      .finally(() => {
        if (!active) return;
        setOrganizerChartStatsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [organizerChartPeriod, token, user?.role]);

  useEffect(() => {
    let active = true;
    if (!token || user?.role !== "organizer") {
      setVisualIdentitySettings(null);
      setVisualIdentityModeDraft("tnp_standard");
      setVisualIdentityColorsDraft(DEFAULT_TNP_VISUAL_IDENTITY_COLORS);
      setVisualIdentityPublicNameDraft("");
      setVisualIdentityLogoDraft("");
      return;
    }
    setVisualIdentityLoading(true);
    setVisualIdentityError("");
    getOrganizerVisualIdentity(token)
      .then((settings) => {
        if (!active) return;
        setVisualIdentitySettings(settings);
        setVisualIdentityModeDraft(
          settings.mode === "custom" ? "custom" : "tnp_standard",
        );
        setVisualIdentityColorsDraft(
          settings.mode === "custom"
            ? settings.colors
            : settings.defaultColors || DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
        );
        setVisualIdentityLogoDraft(settings.logoUrl || "");
        setVisualIdentityPublicNameDraft(settings.displayName || user?.name || "");
      })
      .catch((err) => {
        if (!active) return;
        setVisualIdentityError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.visualIdentity.loadError,
        );
      })
      .finally(() => {
        if (active) setVisualIdentityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user?.name, user?.role]);

  const updateOrganizerRankingPoint = useCallback(
    (
      modality: keyof OrganizerRankingPointsTable,
      key: string,
      value: string,
    ) => {
      const normalized = String(value || "").trim();
      if (normalized && !/^\d+$/.test(normalized)) return;
      setOrganizerRankingDraft((current) => {
        if (!current) return current;
        return {
          ...current,
          [modality]: {
            ...current[modality],
            [key]: normalized ? Number(normalized) : "",
          },
        };
      });
    },
    [],
  );

  const validateOrganizerRankingDraft = useCallback(() => {
    if (!organizerRankingDraft) return false;
    return ORGANIZER_RANKING_FIELDS.every((group) =>
      group.fields.every((field) => {
        const value =
          organizerRankingDraft[group.modality][
            field.key as keyof (typeof organizerRankingDraft)[typeof group.modality]
          ];
        if (value === "" || value === null || value === undefined) return false;
        const numberValue = Number(value);
        return Number.isInteger(numberValue) && numberValue >= 0;
      }),
    );
  }, [organizerRankingDraft]);

  const applyOrganizerRankingSettings = useCallback(
    (settings: OrganizerRankingSettings, mode?: "tnp_standard" | "custom") => {
      setOrganizerRankingSettings(settings);
      setOrganizerRankingDraft(cloneOrganizerRankingTable(settings.points));
      setOrganizerRankingModeDraft(
        mode || (settings.mode === "custom" ? "custom" : "tnp_standard"),
      );
    },
    [],
  );

  const handleOrganizerRankingModeChange = useCallback(
    async (mode: "tnp_standard" | "custom") => {
      if (!token) return;
      setOrganizerRankingModeDraft(mode);
      setOrganizerRankingError("");
      setOrganizerRankingSuccess("");
      if (mode === "tnp_standard") {
        setOrganizerRankingDraft(
          cloneOrganizerRankingTable(
            organizerRankingSettings?.officialPoints ||
              organizerRankingSettings?.points ||
              null,
          ),
        );
        if (organizerRankingSettings?.mode === "custom") {
          setOrganizerRankingSaving(true);
          try {
            const settings = await resetOrganizerRankingSettings(token);
            applyOrganizerRankingSettings(settings, "tnp_standard");
            setOrganizerRankingSuccess(
              siteTexts.profile.user.organizerRanking.resetSuccess,
            );
          } catch (err) {
            setOrganizerRankingError(
              err instanceof Error
                ? err.message
                : siteTexts.profile.user.organizerRanking.saveError,
            );
          } finally {
            setOrganizerRankingSaving(false);
          }
        }
        return;
      }
      if (mode === "custom" && organizerRankingSettings?.hasCustomTable) {
        setOrganizerRankingDraft(
          cloneOrganizerRankingTable(organizerRankingSettings.points),
        );
        return;
      }
      if (mode === "custom") {
        setOrganizerRankingDraft(createEmptyOrganizerRankingTable());
        return;
      }
    },
    [applyOrganizerRankingSettings, organizerRankingSettings, token],
  );

  const saveOrganizerRanking = useCallback(async () => {
    if (!token || !organizerRankingDraft) return;
    setOrganizerRankingError("");
    setOrganizerRankingSuccess("");
    if (organizerRankingModeDraft === "tnp_standard") {
      setOrganizerRankingSaving(true);
      try {
        const settings = await resetOrganizerRankingSettings(token);
        applyOrganizerRankingSettings(settings, "tnp_standard");
        setOrganizerRankingSuccess(
          siteTexts.profile.user.organizerRanking.resetSuccess,
        );
      } catch (err) {
        setOrganizerRankingError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.organizerRanking.saveError,
        );
      } finally {
        setOrganizerRankingSaving(false);
      }
      return;
    }
    if (!validateOrganizerRankingDraft()) {
      setOrganizerRankingError(
        siteTexts.profile.user.organizerRanking.validationError,
      );
      return;
    }
    setOrganizerRankingSaving(true);
    try {
      const settings = await saveOrganizerRankingSettings(token, {
        mode: "custom",
        points: organizerRankingDraft,
      });
      applyOrganizerRankingSettings(settings, "custom");
      setOrganizerRankingSuccess(
        siteTexts.profile.user.organizerRanking.saveSuccess,
      );
    } catch (err) {
      setOrganizerRankingError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.organizerRanking.saveError,
      );
    } finally {
      setOrganizerRankingSaving(false);
    }
  }, [
    applyOrganizerRankingSettings,
    organizerRankingDraft,
    organizerRankingModeDraft,
    token,
    validateOrganizerRankingDraft,
  ]);

  const duplicateOrganizerRankingFromOfficial = useCallback(() => {
    if (!organizerRankingSettings?.officialPoints) return;
    const shouldConfirm =
      organizerRankingModeDraft === "custom" &&
      (organizerRankingSettings.hasCustomTable ||
        hasOrganizerRankingPoint(organizerRankingDraft));
    if (shouldConfirm) {
      const confirmed = window.confirm(
        siteTexts.profile.user.organizerRanking.replaceConfirm,
      );
      if (!confirmed) return;
    }
    setOrganizerRankingModeDraft("custom");
    setOrganizerRankingDraft(
      cloneOrganizerRankingTable(organizerRankingSettings.officialPoints),
    );
    setOrganizerRankingError("");
    setOrganizerRankingSuccess(
      siteTexts.profile.user.organizerRanking.duplicatedNotice,
    );
  }, [
    organizerRankingDraft,
    organizerRankingModeDraft,
    organizerRankingSettings,
  ]);

  const restoreOrganizerRankingDefault = useCallback(async () => {
    if (!token) return;
    const confirmed = window.confirm(
      siteTexts.profile.user.organizerRanking.restoreConfirm,
    );
    if (!confirmed) return;
    setOrganizerRankingSaving(true);
    setOrganizerRankingError("");
    setOrganizerRankingSuccess("");
    try {
      const settings = await resetOrganizerRankingSettings(token);
      applyOrganizerRankingSettings(settings, "tnp_standard");
      setOrganizerRankingSuccess(
        siteTexts.profile.user.organizerRanking.resetSuccess,
      );
    } catch (err) {
      setOrganizerRankingError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.organizerRanking.saveError,
      );
    } finally {
      setOrganizerRankingSaving(false);
    }
  }, [applyOrganizerRankingSettings, token]);

  const applyVisualIdentitySettings = useCallback(
    (
      settings: OrganizerVisualIdentitySettings,
      mode?: "tnp_standard" | "custom",
    ) => {
      setVisualIdentitySettings(settings);
      const nextMode =
        mode || (settings.mode === "custom" ? "custom" : "tnp_standard");
      setVisualIdentityModeDraft(nextMode);
      setVisualIdentityColorsDraft(
        nextMode === "custom"
          ? settings.colors
          : settings.defaultColors || DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
      );
      setVisualIdentityBackgroundDraft(settings.backgroundMode || "blue");
      setVisualIdentityLogoDraft(settings.logoUrl || "");
      setVisualIdentityPublicNameDraft(settings.displayName || user?.name || "");
    },
    [user?.name],
  );

  const handleVisualIdentityModeChange = useCallback(
    (mode: "tnp_standard" | "custom") => {
      setVisualIdentityModeDraft(mode);
      setVisualIdentityError("");
      if (mode === "tnp_standard") {
        setVisualIdentityBackgroundDraft("blue");
        setVisualIdentityColorsDraft(
          visualIdentitySettings?.defaultColors ||
            DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
        );
        return;
      }
      setVisualIdentityColorsDraft(
        visualIdentitySettings?.isCustom
          ? visualIdentitySettings.colors
          : DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
      );
    },
    [visualIdentitySettings],
  );

  const updateVisualIdentityColor = useCallback(
    (field: keyof OrganizerVisualIdentityColors, value: string) => {
      setVisualIdentityColorsDraft((current) => ({
        ...current,
        [field]: value.startsWith("#")
          ? value.slice(0, 7)
          : `#${value.slice(0, 6)}`,
      }));
      setVisualIdentityError("");
    },
    [],
  );

  const handleOrganizerLogoFile = useCallback((file?: File | null) => {
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setVisualIdentityError(siteTexts.profile.user.visualIdentity.invalidLogo);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setVisualIdentityError(
        siteTexts.profile.user.visualIdentity.logoTooLarge,
      );
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (
        image.naturalWidth < 64 ||
        image.naturalHeight < 64 ||
        image.naturalWidth > 4096 ||
        image.naturalHeight > 4096
      ) {
        setVisualIdentityError(
          siteTexts.profile.user.visualIdentity.logoInvalidDimensions,
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setVisualIdentityLogoDraft(String(reader.result || ""));
        setVisualIdentityError("");
      };
      reader.onerror = () => {
        setVisualIdentityError(siteTexts.profile.user.visualIdentity.invalidLogo);
      };
      reader.readAsDataURL(file);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setVisualIdentityError(siteTexts.profile.user.visualIdentity.invalidLogo);
    };
    image.src = objectUrl;
  }, []);

  const removeOrganizerLogo = useCallback(() => {
    setVisualIdentityLogoDraft("");
    setVisualIdentityError("");
    if (organizerLogoInputRef.current) {
      organizerLogoInputRef.current.value = "";
    }
  }, []);

  const saveVisualIdentity = useCallback(async () => {
    if (!token) return;
    setVisualIdentityError("");
    setVisualIdentitySaving(true);
    try {
      if (visualIdentityModeDraft === "tnp_standard") {
        const settings = await saveOrganizerVisualIdentity(token, {
          mode: "tnp_standard",
          logoUrl: visualIdentityLogoDraft,
          displayName: visualIdentityPublicNameDraft,
          backgroundMode: visualIdentityBackgroundDraft,
        });
        applyVisualIdentitySettings(settings, "tnp_standard");
        setToast({
          message: siteTexts.profile.user.visualIdentity.resetSuccess,
          variant: "success",
        });
        return;
      }

      const normalizedColors = {
        primaryColor: normalizeHexColor(visualIdentityColorsDraft.primaryColor),
        secondaryColor: normalizeHexColor(
          visualIdentityColorsDraft.secondaryColor,
        ),
        accentColor: normalizeHexColor(visualIdentityColorsDraft.accentColor),
      };
      if (
        !isValidHexColor(normalizedColors.primaryColor) ||
        !isValidHexColor(normalizedColors.secondaryColor) ||
        !isValidHexColor(normalizedColors.accentColor)
      ) {
        setVisualIdentityError(
          siteTexts.profile.user.visualIdentity.validationError,
        );
        return;
      }
      const settings = await saveOrganizerVisualIdentity(token, {
        mode: "custom",
        colors: normalizedColors,
        logoUrl: visualIdentityLogoDraft,
        displayName: visualIdentityPublicNameDraft,
        backgroundMode: visualIdentityBackgroundDraft,
      });
      applyVisualIdentitySettings(settings, "custom");
      setToast({
        message: siteTexts.profile.user.visualIdentity.saveSuccess,
        variant: "success",
      });
    } catch (err) {
      setToast({
        message:
          err instanceof Error
            ? err.message
            : siteTexts.profile.user.visualIdentity.saveError,
        variant: "error",
      });
    } finally {
      setVisualIdentitySaving(false);
    }
  }, [
    applyVisualIdentitySettings,
    token,
    visualIdentityColorsDraft,
    visualIdentityBackgroundDraft,
    visualIdentityLogoDraft,
    visualIdentityModeDraft,
    visualIdentityPublicNameDraft,
  ]);

  const hasProfileChanges = useMemo(() => {
    if (!user) return false;
    return (
      String(nameDraft).trim() !== String(user.name || "").trim() ||
      String(phoneDraft).trim() !== String(user.phone || "").trim() ||
      String(sexDraft || "") !== String(user.sex || "") ||
      String(categoryDraft || "") !== String(user.category || "") ||
      String(clubIdDraft || "") !== String(user.clubId || "") ||
      String(sidePreferenceDraft || "ambos") !== String(savedSidePreference || "ambos") ||
      String(avatarDraft || "") !== String(user.avatarUrl || "")
    );
  }, [
    avatarDraft,
    categoryDraft,
    clubIdDraft,
    nameDraft,
    phoneDraft,
    savedSidePreference,
    sexDraft,
    sidePreferenceDraft,
    user,
  ]);

  const hasEmailChange = useMemo(() => {
    if (!user) return false;
    return (
      String(securityEmailDraft || "")
        .trim()
        .toLowerCase() !==
      String(user.email || "")
        .trim()
        .toLowerCase()
    );
  }, [securityEmailDraft, user]);

  const categoryIsLocked = useMemo(
    () => Boolean(String(user?.category || "").trim()),
    [user?.category],
  );

  useEffect(() => {
    let active = true;
    if (!token || !user || !categoryIsLocked) {
      setPendingCategoryRequest(null);
      setCategoryChangeRequests([]);
      return () => {
        active = false;
      };
    }
    setCategoryRequestLoading(true);
    listMyCategoryChangeRequests(token, {
      status: "all",
    })
      .then((requests) => {
        if (!active) return;
        setCategoryChangeRequests(requests);
        setPendingCategoryRequest(
          requests.find((request) => request.status === "pending") || null,
        );
      })
      .catch(() => {
        if (!active) return;
        setPendingCategoryRequest(null);
        setCategoryChangeRequests([]);
      })
      .finally(() => {
        if (!active) return;
        setCategoryRequestLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token, user, categoryIsLocked]);

  const sortedCategoryChangeRequests = useMemo(
    () =>
      [...categoryChangeRequests].sort((left, right) => {
        const rightTime = new Date(String(right.createdAt || "")).getTime();
        const leftTime = new Date(String(left.createdAt || "")).getTime();
        const safeRightTime = Number.isFinite(rightTime) ? rightTime : 0;
        const safeLeftTime = Number.isFinite(leftTime) ? leftTime : 0;
        if (safeRightTime !== safeLeftTime) {
          return safeRightTime - safeLeftTime;
        }
        return Number(right.id || 0) - Number(left.id || 0);
      }),
    [categoryChangeRequests],
  );

  const myOpenMatchGroups = useMemo(() => {
    const groupedMatches: Record<ProfileOpenMatchGroupKey, OpenMatchItem[]> = {
      created: [],
      confirmed: [],
      invites: [],
      pending: [],
      history: [],
      canceled: [],
    };
    const nowTime = Date.now();

    myOpenMatches.forEach((match) => {
      const participantStatus = match.currentUserParticipant?.status || "";
      if (match.status === "canceled") {
        groupedMatches.canceled.push(match);
        return;
      }
      if (isHistoricalOpenMatch(match, nowTime)) {
        groupedMatches.history.push(match);
        return;
      }
      if (user?.id === match.organizer.id) {
        groupedMatches.created.push(match);
        return;
      }
      if (participantStatus === "invited") {
        groupedMatches.invites.push(match);
        return;
      }
      if (participantStatus === "confirmed") {
        groupedMatches.confirmed.push(match);
        return;
      }
      if (participantStatus === "pending") {
        groupedMatches.pending.push(match);
        return;
      }
    });

    return PROFILE_OPEN_MATCH_GROUP_KEYS.map((key) => ({
      key,
      title: siteTexts.profile.openMatches.groups[key],
      matches: sortProfileOpenMatches(groupedMatches[key]),
    }));
  }, [myOpenMatches, user?.id]);

  const myOpenMatchesTotal = useMemo(
    () =>
      myOpenMatchGroups.reduce(
        (total, group) => total + group.matches.length,
        0,
      ),
    [myOpenMatchGroups],
  );
  const myGamesTournamentIds = useMemo(() => {
    const ids = new Set<number>();
    [...myGamesUpcoming, ...myGamesHistory].forEach((game) => {
      const tournamentId = Number(game.tournamentId || 0);
      if (Number.isFinite(tournamentId) && tournamentId > 0) {
        ids.add(tournamentId);
      }
    });
    return ids;
  }, [myGamesHistory, myGamesUpcoming]);
  const selectedModalUpcomingGames = useMemo(
    () =>
      myGamesModalTournamentId
        ? myGamesUpcoming.filter(
            (game) =>
              Number(game.tournamentId || 0) === myGamesModalTournamentId,
          )
        : [],
    [myGamesModalTournamentId, myGamesUpcoming],
  );
  const selectedModalHistoryGames = useMemo(
    () =>
      myGamesModalTournamentId
        ? myGamesHistory.filter(
            (game) =>
              Number(game.tournamentId || 0) === myGamesModalTournamentId,
          )
        : [],
    [myGamesHistory, myGamesModalTournamentId],
  );
  const selectedModalTournamentName = useMemo(() => {
    if (!myGamesModalTournamentId) return "";
    return (
      [...selectedModalUpcomingGames, ...selectedModalHistoryGames]
        .map((game) => String(game.tournamentName || "").trim())
        .find(Boolean) || ""
    );
  }, [
    myGamesModalTournamentId,
    selectedModalHistoryGames,
    selectedModalUpcomingGames,
  ]);
  const openMyGamesModalForTournament = useCallback((tournamentId: number) => {
    const normalizedTournamentId = Number(tournamentId || 0);
    if (
      !Number.isFinite(normalizedTournamentId) ||
      normalizedTournamentId <= 0
    ) {
      return;
    }
    setTab("profile");
    setShowCategoryRequestForm(false);
    setOpenProfileAccordions((current) => ({
      ...current,
      registrations: true,
    }));
    setMyGamesModalTournamentId(normalizedTournamentId);
  }, []);

  useEffect(() => {
    let active = true;
    if (!token || !user) {
      setMyGamesUpcoming([]);
      setMyGamesHistory([]);
      return () => {
        active = false;
      };
    }
    setMyGamesLoading(true);
    setMyGamesError("");
    listMyGames(token)
      .then((response) => {
        if (!active) return;
        setMyGamesUpcoming(response.upcoming || []);
        setMyGamesHistory(response.history || []);
      })
      .catch((err) => {
        if (!active) return;
        setMyGamesUpcoming([]);
        setMyGamesHistory([]);
        setMyGamesError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.myGames.loadError,
        );
      })
      .finally(() => {
        if (!active) return;
        setMyGamesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user]);

  useEffect(() => {
    let active = true;
    if (!token || !user) {
      setMyOpenMatches([]);
      setMyOpenMatchesLoading(false);
      setMyOpenMatchesError("");
      return () => {
        active = false;
      };
    }
    setMyOpenMatchesLoading(true);
    setMyOpenMatchesError("");
    listMyMatches(token)
      .then((response) => {
        if (!active) return;
        setMyOpenMatches(response.matches || []);
      })
      .catch((err) => {
        if (!active) return;
        setMyOpenMatches([]);
        setMyOpenMatchesError(
          err instanceof Error
            ? err.message
            : siteTexts.profile.openMatches.loadError,
        );
      })
      .finally(() => {
        if (!active) return;
        setMyOpenMatchesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user]);

  useEffect(() => {
    if (!editingOpenMatch || openMatchClubs.length > 0) return;
    let active = true;
    setOpenMatchClubsLoading(true);
    listClubs({})
      .then((response) => {
        if (!active) return;
        setOpenMatchClubs(response.clubs || []);
      })
      .catch(() => {
        if (!active) return;
        setOpenMatchClubs([]);
      })
      .finally(() => {
        if (!active) return;
        setOpenMatchClubsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [editingOpenMatch, openMatchClubs.length]);

  useEffect(() => {
    const readProfileSection = () => {
      const hash = String(window.location.hash || "");
      const queryIndex = hash.indexOf("?");
      const params =
        queryIndex >= 0 ? new URLSearchParams(hash.slice(queryIndex + 1)) : null;
      const sectionKey = resolveProfileSectionAccordionKey(
        params?.get("section"),
      );
      if (!sectionKey) {
        return;
      }
      setTab("profile");
      setShowCategoryRequestForm(false);
      if (sectionKey === "myGames") {
        const tournamentId = Number(
          params?.get("tournamentId") || params?.get("competitionId") || 0,
        );
        if (Number.isFinite(tournamentId) && tournamentId > 0) {
          setOpenProfileAccordions({
            ...CLOSED_PROFILE_ACCORDIONS,
            registrations: true,
          });
          setMyGamesModalTournamentId(tournamentId);
          return;
        }
      }
      setOpenProfileAccordions({
        ...CLOSED_PROFILE_ACCORDIONS,
        [sectionKey]: true,
      });
    };
    readProfileSection();
    window.addEventListener("hashchange", readProfileSection);
    return () => window.removeEventListener("hashchange", readProfileSection);
  }, []);

  const openCategoryRequestModal = () => {
    setCategoryRequestSuccess("");
    setCategoryRequestError("");
    setRequestedCategoryDraft("");
    setCategoryRequestReasonDraft("");
    setCategoryRequestObservationDraft("");
    setOpenProfileAccordions({ ...CLOSED_PROFILE_ACCORDIONS });
    setShowCategoryRequestForm(true);
  };

  const onRequestCategoryChange = async () => {
    setCategoryRequestError("");
    setCategoryRequestSuccess("");
    if (!token || !user) return;
    if (categoryRequestLoading) {
      setCategoryRequestError(siteTexts.profile.user.loadingCategoryRequests);
      return;
    }
    if (pendingCategoryRequest) {
      setCategoryRequestError(siteTexts.profile.user.dashboard.pendingRequest);
      return;
    }
    if (!requestedCategoryDraft) {
      setCategoryRequestError(
        siteTexts.profile.user.categoryRequest.categoryRequired,
      );
      return;
    }
    if (!categoryRequestReasonDraft.trim()) {
      setCategoryRequestError(
        siteTexts.profile.user.dashboard.requestReasonRequired,
      );
      return;
    }
    if (requestedCategoryDraft === user.category) {
      setCategoryRequestError(
        siteTexts.profile.user.categoryRequest.differentCategory,
      );
      return;
    }
    try {
      setCategoryRequestSaving(true);
      const reasonParts = [
        categoryRequestReasonDraft.trim(),
        categoryRequestObservationDraft.trim()
          ? `${siteTexts.profile.user.categoryRequest.observationPrefix} ${categoryRequestObservationDraft.trim()}`
          : "",
      ].filter(Boolean);
      const response = await requestCategoryChange(token, {
        requestedCategory: requestedCategoryDraft,
        reason: reasonParts.join("\n\n"),
      });
      setPendingCategoryRequest(response.request || null);
      setCategoryChangeRequests((prev) => {
        const request = response.request;
        if (!request) return prev;
        return [
          request,
          ...prev.filter((item) => Number(item.id) !== Number(request.id)),
        ];
      });
      setCategoryRequestReasonDraft("");
      setCategoryRequestObservationDraft("");
      setRequestedCategoryDraft("");
      setShowCategoryRequestForm(false);
      setCategoryRequestSuccess(siteTexts.profile.user.categoryRequest.success);
    } catch (err) {
      setCategoryRequestError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.categoryRequest.error,
      );
    } finally {
      setCategoryRequestSaving(false);
    }
  };

  const onSaveNotificationPreferences = async () => {
    if (!token) return;
    setNotificationPreferencesSaving(true);
    setNotificationPreferencesError("");
    try {
      const saved = await saveNotificationPreferences(
        token,
        notificationPreferences,
      );
      setNotificationPreferences(saved);
      setToast({
        message: siteTexts.profile.user.notificationPreferences.saveSuccess,
        variant: "success",
      });
    } catch (err) {
      setNotificationPreferencesError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.notificationPreferences.saveError,
      );
    } finally {
      setNotificationPreferencesSaving(false);
    }
  };

  const onSaveProfile = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (!user) return;
    try {
      setSaving(true);
      const payload: Partial<
        Pick<
          AuthUser,
          "name" | "phone" | "sex" | "category" | "clubId" | "avatarUrl"
        >
      > = {};
      const nextName = String(nameDraft || "").trim();
      const currentName = String(user.name || "").trim();
      if (nextName !== currentName) payload.name = nextName;

      const nextPhone = String(phoneDraft || "").trim();
      const currentPhone = String(user.phone || "").trim();
      if (nextPhone !== currentPhone) payload.phone = nextPhone;

      const nextSex = String(sexDraft || "");
      const currentSex = String(user.sex || "");
      if (nextSex !== currentSex) {
        payload.sex = sexDraft;
      }

      const nextCategory = String(categoryDraft || "");
      const currentCategory = String(user.category || "");
      if (nextCategory !== currentCategory) {
        payload.category = categoryDraft;
      }

      const nextClubId =
        clubIdDraft && Number(clubIdDraft) > 0
          ? Math.floor(Number(clubIdDraft))
          : null;
      const currentClubId = user.clubId ? Math.floor(Number(user.clubId)) : null;
      if (nextClubId !== currentClubId) {
        payload.clubId = nextClubId;
      }

      const nextAvatarUrl = String(avatarDraft || "").trim();
      const currentAvatarUrl = String(user.avatarUrl || "").trim();
      if (nextAvatarUrl !== currentAvatarUrl) {
        payload.avatarUrl = nextAvatarUrl;
      }

      const nextSidePreference = sidePreferenceDraft || "ambos";
      const shouldUpdateSidePreference =
        nextSidePreference !== (savedSidePreference || "ambos");

      if (Object.keys(payload).length === 0 && !shouldUpdateSidePreference) {
        setToast({
          message: siteTexts.profile.user.noChanges,
          variant: "success",
        });
        return;
      }

      if (Object.keys(payload).length > 0) {
        await updateProfile(payload);
      }
      if (shouldUpdateSidePreference) {
        const savedSide = await updatePlayerSidePreference(
          token || "",
          user.cpf,
          nextName || user.name,
          nextSidePreference,
        );
        setSavedSidePreference(savedSide || "ambos");
        setSidePreferenceDraft(savedSide || "ambos");
      }
      setToast({
        message: siteTexts.profile.user.saveSuccess,
        variant: "success",
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : siteTexts.profile.user.saveError;
      if (
        message.includes("Cannot PATCH /api/auth/profile") ||
        message.includes("Cannot PATCH /api/auth/account")
      ) {
        setError(siteTexts.profile.user.outdatedServer);
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const onSaveAvatarCrop = async (nextAvatarUrl: string) => {
    setError("");
    if (!user) return;
    try {
      setSaving(true);
      const updated = await updateProfile({
        avatarUrl: String(nextAvatarUrl || "").trim(),
      });
      setAvatarDraft(String(updated.avatarUrl || ""));
      setToast({
        message: siteTexts.profile.user.avatarSaveSuccess,
        variant: "success",
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : siteTexts.profile.user.saveError;
      setError(message);
      setToast({
        message,
        variant: "error",
      });
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  const onSaveSecurityEmail = async (event: FormEvent) => {
    event.preventDefault();
    setEmailError("");
    if (!user) return;
    try {
      setEmailSaving(true);
      const updated = await updateProfile({
        email: String(securityEmailDraft || "").trim(),
      });
      setToast({
        message: updated?.emailChangeRequested
          ? `${siteTexts.profile.user.security.emailRequestRegisteredPrefix} ${updated.pendingEmail || "-"}.`
          : siteTexts.profile.user.security.emailUpdated,
        variant: "success",
      });
    } catch (err) {
      setEmailError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.security.emailUpdateError,
      );
    } finally {
      setEmailSaving(false);
    }
  };

  const onRequestEmailConfirmationLink = async () => {
    setEmailError("");
    try {
      setEmailLinkLoading(true);
      await requestEmailConfirmationLink();
      setToast({
        message: siteTexts.profile.user.security.confirmationLinkSuccess,
        variant: "success",
      });
    } catch (err) {
      setEmailError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.security.confirmationLinkError,
      );
    } finally {
      setEmailLinkLoading(false);
    }
  };

  const onChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    if (!currentPasswordDraft || !newPasswordDraft) {
      setPasswordError(siteTexts.profile.user.security.passwordRequired);
      return;
    }
    if (newPasswordDraft.length < 4) {
      setPasswordError(siteTexts.profile.user.security.passwordMinLength);
      return;
    }
    if (newPasswordDraft !== newPasswordConfirmDraft) {
      setPasswordError(siteTexts.profile.user.security.passwordMismatch);
      return;
    }
    try {
      setPasswordSaving(true);
      await changePassword(currentPasswordDraft, newPasswordDraft);
      setCurrentPasswordDraft("");
      setNewPasswordDraft("");
      setNewPasswordConfirmDraft("");
      setPasswordSuccess(siteTexts.profile.user.security.passwordUpdated);
    } catch (err) {
      setPasswordError(
        err instanceof Error
          ? err.message
          : siteTexts.profile.user.security.passwordUpdateError,
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  const showSuccessToast = (message: string) => {
    const text = String(message || "").trim();
    if (!text) return;
    setToast({ message: text, variant: "success" });
  };

  const showErrorToast = (message: string) => {
    const text = String(message || "").trim();
    if (!text) return;
    setToast({ message: text, variant: "error" });
  };

  const onUpdateOpenMatch = async (payload: CreateOpenMatchPayload) => {
    if (!token || !editingOpenMatch) {
      throw new Error(siteTexts.openMatches.actions.createLogin);
    }
    await updateMatch(token, editingOpenMatch.id, payload);
    const response = await listMyMatches(token);
    setMyOpenMatches(response.matches || []);
    setEditingOpenMatch(null);
    showSuccessToast(siteTexts.profile.openMatches.updateSuccess);
  };

  const onReviewOpenMatchParticipant = async (
    matchId: number,
    participantId: number,
    status: OpenMatchParticipantStatus,
  ) => {
    if (!token) return;
    try {
      setOpenMatchParticipantActionId(participantId);
      const response = await reviewMatchParticipant(
        token,
        matchId,
        participantId,
        status,
      );
      if (response.match) {
        setMyOpenMatches((current) =>
          current.map((match) =>
            Number(match.id) === Number(response.match.id)
              ? response.match
              : match,
          ),
        );
      }
      showSuccessToast(siteTexts.openMatches.detail.reviewSuccess);
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : siteTexts.openMatches.detail.reviewError,
      );
    } finally {
      setOpenMatchParticipantActionId(null);
    }
  };

  const onCancelRegistration = async (registrationId: number) => {
    if (!token) return;
    try {
      setRegistrationActionId(registrationId);
      await cancelMyEventRegistration(token, registrationId);
      setMyRegistrations((prev) =>
        prev.filter((item) => Number(item.id) !== Number(registrationId)),
      );
      showSuccessToast(siteTexts.profile.registrations.toasts.cancelSuccess);
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : siteTexts.profile.registrations.toasts.cancelError,
      );
    } finally {
      setRegistrationActionId((current) =>
        Number(current) === Number(registrationId) ? null : current,
      );
    }
  };

  const onDeleteRegistration = async (registrationId: number) => {
    if (!token) return;
    try {
      setRegistrationActionId(registrationId);
      await deleteMyEventRegistration(token, registrationId);
      setMyRegistrations((prev) =>
        prev.filter((item) => Number(item.id) !== Number(registrationId)),
      );
      showSuccessToast(siteTexts.profile.registrations.toasts.deleteSuccess);
    } catch (err) {
      showErrorToast(
        err instanceof Error
          ? err.message
          : siteTexts.profile.registrations.toasts.deleteError,
      );
    } finally {
      setRegistrationActionId((current) =>
        Number(current) === Number(registrationId) ? null : current,
      );
    }
  };

  useEffect(() => {
    if (!token || !user) return;
    let active = true;
    setChallengeLoading(true);
    listMyChallenges(token, {
      name: nameFilter,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    })
      .then((data) => {
        if (!active) return;
        setChallenges(data);
      })
      .catch(() => {
        if (!active) return;
        setChallenges([]);
      })
      .finally(() => {
        if (active) setChallengeLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token, user, nameFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!token || !user) {
      setEventAchievements({ titles: 0, runnersUp: 0 });
      return;
    }
    const userCpf = normalizeProfileCpf(user.cpf);
    const finishedChallenges = challenges.filter(
      (item) => item.status === "finished",
    );
    if (!userCpf || finishedChallenges.length === 0) {
      setEventAchievements({ titles: 0, runnersUp: 0 });
      return;
    }

    let active = true;
    Promise.all(
      finishedChallenges.map((item) =>
        getChallengeByIdMaybeAuth(Number(item.id), token).catch(() => null),
      ),
    ).then((details) => {
      if (!active) return;
      const achievements = details.reduce<ProfileEventAchievements>(
        (total, detail) => {
          if (!detail) return total;
          const position = calculateProfileEventPosition(detail, userCpf);
          if (position === 1) {
            return { ...total, titles: total.titles + 1 };
          }
          if (position === 2) {
            return { ...total, runnersUp: total.runnersUp + 1 };
          }
          return total;
        },
        { titles: 0, runnersUp: 0 },
      );
      setEventAchievements(achievements);
    });

    return () => {
      active = false;
    };
  }, [challenges, token, user]);

  useEffect(() => {
    if (!user?.id) {
      setRankingPosition(null);
      setProfileSharePublicProfile(null);
      return;
    }
    let active = true;
    getPublicPlayerProfile(user.id)
      .then((response) => {
        if (!active) return;
        setProfileSharePublicProfile(response);
        setRankingPosition(getCurrentRankingPositionFromPublicProfile(response));
      })
      .catch(() => {
        if (!active) return;
        setProfileSharePublicProfile(null);
        setRankingPosition(null);
      });

    return () => {
      active = false;
    };
  }, [user?.category, user?.id]);

  useEffect(() => {
    if (!token || !user) return;
    let active = true;
    setMyRegistrationsLoading(true);
    listMyEventRegistrations(token)
      .then((rows) => {
        if (!active) return;
        setMyRegistrations(rows || []);
      })
      .catch(() => {
        if (!active) return;
        setMyRegistrations([]);
      })
      .finally(() => {
        if (active) setMyRegistrationsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, user]);

  const filteredChallenges = challenges.filter(
    (item) =>
      (eventStatusFilter === "all" || item.status === eventStatusFilter) &&
      (eventTypeFilter === "all" || item.challengeType === eventTypeFilter),
  );
  const profileEventStats = useMemo(
    () => ({
      events: challenges.length,
      matches: challenges.reduce(
        (total, item) => total + Math.max(0, Number(item.matchesCount || 0)),
        0,
      ),
      titles: eventAchievements.titles,
      runnersUp: eventAchievements.runnersUp,
      category: localizedCategoryLabel(user?.category || ""),
      rankingPosition,
    }),
    [challenges, eventAchievements, rankingPosition, user?.category],
  );
  const profileShareLink = useMemo(
    () => buildPublicPlayerProfileUrl(user?.id),
    [user?.id],
  );
  const profileShareStats = useMemo<ProfileShareStats>(
    () => ({
      matches: profileEventStats.matches,
      titles: profileEventStats.titles,
      runnersUp: profileEventStats.runnersUp,
      category: profileEventStats.category,
      rankingPosition: profileEventStats.rankingPosition,
    }),
    [
      profileEventStats.category,
      profileEventStats.matches,
      profileEventStats.rankingPosition,
      profileEventStats.runnersUp,
      profileEventStats.titles,
    ],
  );
  const createProfileSharePreview = useCallback(async () => {
    if (!user) return;
    setProfileShareLoading(true);
    setProfileShareStatus("");
    try {
      let publicProfile = profileSharePublicProfile;
      if (!publicProfile && user.id) {
        publicProfile = await getPublicPlayerProfile(user.id).catch(() => null);
        if (publicProfile) setProfileSharePublicProfile(publicProfile);
      }
      const preview = await createProfileShareBlob({
        user,
        stats: profileShareStats,
        avatarUrl: avatarDraft || user.avatarUrl,
        achievements,
        publicProfile,
        publicProfileUrl: profileShareLink,
      });
      setProfileSharePreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return {
          ...preview,
          url: URL.createObjectURL(preview.blob),
        };
      });
    } catch (_error) {
      setProfileShareStatus(
        siteTexts.profile.user.dashboard.share.generateError,
      );
    } finally {
      setProfileShareLoading(false);
    }
  }, [
    achievements,
    avatarDraft,
    profileShareLink,
    profileSharePublicProfile,
    profileShareStats,
    user,
  ]);
  const copyProfileShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(profileShareLink);
      setProfileShareStatus(siteTexts.profile.user.dashboard.share.copied);
    } catch (_error) {
      setProfileShareStatus(siteTexts.profile.user.dashboard.share.copyError);
    }
  }, [profileShareLink]);
  const downloadProfileShare = useCallback(() => {
    if (!profileSharePreview) return;
    downloadBlob(profileSharePreview.blob, profileSharePreview.filename);
  }, [profileSharePreview]);
  const shareProfile = useCallback(async () => {
    if (!profileSharePreview || !user) return;
    const file = new File(
      [profileSharePreview.blob],
      profileSharePreview.filename,
      { type: "image/png" },
    );
    const payload = {
      files: [file],
      title: siteTexts.profile.user.dashboard.share.shareTitle,
      text: `${siteTexts.profile.user.dashboard.share.journey} - ${profileShareLink}`,
    };
    if (navigator.share && navigator.canShare?.(payload)) {
      await navigator.share(payload);
      return;
    }
    downloadBlob(profileSharePreview.blob, profileSharePreview.filename);
    setProfileShareStatus(
      siteTexts.profile.user.dashboard.share.directUnavailable,
    );
  }, [profileShareLink, profileSharePreview, user]);
  const toggleProfileAccordion = useCallback(
    (key: UserProfileAccordionKey, forceOpen = false) => {
      setMobileProfileInfoOpen(false);
      setOpenProfileAccordions((current) => ({
        ...CLOSED_PROFILE_ACCORDIONS,
        [key]: forceOpen ? true : !current[key],
      }));
    },
    [],
  );
  const navigateProfileAccordion = useCallback(
    (key: UserProfileAccordionKey) => {
      setTab("profile");
      setShowCategoryRequestForm(false);
      setMobileProfileInfoOpen(false);
      setOpenProfileAccordions({
        ...CLOSED_PROFILE_ACCORDIONS,
        [key]: true,
      });
    },
    [],
  );
  const hasActiveProfileAccordion = Object.values(openProfileAccordions).some(
    Boolean,
  );

  useEffect(() => {
    if (!profileShareOpen) return;
    void createProfileSharePreview();
  }, [createProfileSharePreview, profileShareOpen]);

  useEffect(() => {
    if (profileShareOpen) return;
    setProfileSharePreview((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
    setProfileShareStatus("");
  }, [profileShareOpen]);

  useEffect(() => {
    if (!profileShareOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setProfileShareOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [profileShareOpen]);

  const visualIdentityPreview = useMemo(
    () => ({
      mode: visualIdentityModeDraft,
      backgroundMode: visualIdentityBackgroundDraft,
      colors: visualIdentityColorsDraft,
      defaultColors:
        visualIdentitySettings?.defaultColors ||
        DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
      isCustom: visualIdentityModeDraft === "custom",
    }),
    [
      visualIdentityColorsDraft,
      visualIdentityBackgroundDraft,
      visualIdentityModeDraft,
      visualIdentitySettings?.defaultColors,
    ],
  );
  const visualIdentityPreviewStyle = useMemo(
    () => organizerIdentityStyle(visualIdentityPreview),
    [visualIdentityPreview],
  );
  const visualIdentityHasLowContrast = useMemo(
    () =>
      visualIdentityModeDraft === "custom" &&
      hasLowOrganizerIdentityContrast(
        visualIdentityColorsDraft,
        visualIdentityBackgroundDraft,
      ),
    [
      visualIdentityBackgroundDraft,
      visualIdentityColorsDraft,
      visualIdentityModeDraft,
    ],
  );

  const eventsPageSize = 4;
  const eventsTotalPages = Math.max(
    1,
    Math.ceil(filteredChallenges.length / eventsPageSize),
  );
  const normalizedEventsPage = Math.min(eventsPage, eventsTotalPages);
  const paginatedChallenges = filteredChallenges.slice(
    (normalizedEventsPage - 1) * eventsPageSize,
    normalizedEventsPage * eventsPageSize,
  );

  useEffect(() => {
    setEventsPage(1);
  }, [eventStatusFilter, eventTypeFilter, nameFilter]);

  const registrationItems = useMemo<RegistrationViewItem[]>(
    () =>
      myRegistrations.map((item) => {
        const visualStatus = normalizeRegistrationVisualStatus(item);
        const metadata = buildRegistrationVisualMetadata(item);
        return {
          ...item,
          visualStatus,
          ...metadata,
        };
      }),
    [myRegistrations],
  );

  const registrationStatusSummary = useMemo(
    () => ({
      all: registrationItems.length,
      confirmed: registrationItems.filter(
        (item) => item.visualStatus === "confirmed",
      ).length,
      pending: registrationItems.filter(
        (item) => item.visualStatus === "pending",
      ).length,
      waitlist: registrationItems.filter(
        (item) => item.visualStatus === "waitlist",
      ).length,
      canceled: registrationItems.filter(
        (item) => item.visualStatus === "canceled",
      ).length,
      overdue: registrationItems.filter(
        (item) => item.visualStatus === "overdue",
      ).length,
    }),
    [registrationItems],
  );

  const filteredRegistrations = useMemo(
    () =>
      registrationItems.filter((item) => {
        if (registrationStatusFilter === "all") return true;
        return item.visualStatus === registrationStatusFilter;
      }),
    [registrationItems, registrationStatusFilter],
  );
  if (loading) {
    return <LoadingPage label={siteTexts.common.loading} />;
  }

  return (
    <AppLayout title="" subtitle="">
      {!loading && !user && (
        <EmptyState
          message={siteTexts.profile.user.emptyMessage}
          action={
            <Button variant="primary" href="#/login">
              {siteTexts.profile.user.goToLogin}
            </Button>
          }
        />
      )}

      {user && (
        <>
          <div className="user-profile-grid">
            <ProfileSidebar
              user={user}
              tab={tab}
              showCategoryRequestForm={showCategoryRequestForm}
              hasActiveProfileAccordion={hasActiveProfileAccordion}
              openProfileAccordions={openProfileAccordions}
              avatarDraft={avatarDraft}
              sexDraft={sexDraft}
              stats={profileEventStats}
              onSaveAvatarCrop={onSaveAvatarCrop}
              onRemoveAvatar={() => setAvatarDraft("")}
              onSelectOverview={() => {
                setTab("profile");
                setShowCategoryRequestForm(false);
                setMobileProfileInfoOpen(false);
                setOpenProfileAccordions({
                  ...CLOSED_PROFILE_ACCORDIONS,
                });
              }}
              onNavigateAccordion={navigateProfileAccordion}
              onOpenShare={() => setProfileShareOpen(true)}
              onOpenCategoryRequest={() => {
                setTab("profile");
                openCategoryRequestModal();
              }}
            />

            <section className="user-profile-panel">
              {tab === "profile" && (
                <div
                  className={`user-profile-card user-profile-overview-card${
                    hasActiveProfileAccordion
                      ? " has-active-profile-section"
                      : ""
                  }${mobileProfileInfoOpen ? " is-mobile-profile-open" : ""}`}
                >
                  {!showCategoryRequestForm && (
                    <>
                      <ProfileOverviewTab
                        user={user}
                        dashboardTexts={dashboardTexts}
                        info={info}
                        open={mobileProfileInfoOpen}
                        categoryIsLocked={categoryIsLocked}
                        clubs={profileClubs}
                        clubsLoading={profileClubsLoading}
                        sidePreferenceLoading={sidePreferenceLoading}
                        saving={saving}
                        hasProfileChanges={hasProfileChanges}
                        error={error}
                        nameDraft={nameDraft}
                        cpfDraft={cpfDraft}
                        phoneDraft={phoneDraft}
                        sexDraft={sexDraft}
                        categoryDraft={categoryDraft}
                        clubIdDraft={clubIdDraft}
                        sidePreferenceDraft={sidePreferenceDraft}
                        onToggle={() =>
                          setMobileProfileInfoOpen((current) => !current)
                        }
                        onSubmit={onSaveProfile}
                        onReset={() => {
                          setError("");
                          setNameDraft(user.name || "");
                          setCpfDraft(formatCpf(user.cpf || ""));
                          setPhoneDraft(user.phone || "");
                          setSexDraft(user.sex || "");
                          setCategoryDraft(user.category || "");
                          setClubIdDraft(user.clubId ? String(user.clubId) : "");
                          setSidePreferenceDraft(savedSidePreference || "ambos");
                          setAvatarDraft(user.avatarUrl || "");
                        }}
                        onNameChange={setNameDraft}
                        onPhoneChange={setPhoneDraft}
                        onSexChange={setSexDraft}
                        onCategoryChange={setCategoryDraft}
                        onClubIdChange={setClubIdDraft}
                        onSidePreferenceChange={setSidePreferenceDraft}
                        localizedCategoryLabel={localizedCategoryLabel}
                        memberSinceLabel={formatMemberSinceLabel(user)}
                      />


                      <ProfileAchievementsTab
                        open={openProfileAccordions.achievements}
                        onToggle={() => toggleProfileAccordion("achievements")}
                        achievements={achievements}
                        loading={achievementsLoading}
                        error={achievementsError}
                      />

                      <ProfileNotificationPreferencesTab
                        open={openProfileAccordions.notifications}
                        onToggle={() => toggleProfileAccordion("notifications")}
                        preferences={notificationPreferences}
                        loading={notificationPreferencesLoading}
                        saving={notificationPreferencesSaving}
                        error={notificationPreferencesError}
                        onPreferencesChange={setNotificationPreferences}
                        onSave={() => void onSaveNotificationPreferences()}
                      />
                      {(user.role === "organizer" || user.role === "admin") && (
                        <ProfileOrganizerStatisticsTab
                          userRole={user.role}
                          open={openProfileAccordions.statistics}
                          period={organizerStatsPeriod}
                          dateFrom={organizerStatsDateFrom}
                          dateTo={organizerStatsDateTo}
                          loading={organizerStatsLoading}
                          error={organizerStatsError}
                          stats={organizerStats}
                          chartStats={organizerChartStats}
                          chartStatsLoading={organizerChartStatsLoading}
                          chartStatsError={organizerChartStatsError}
                          chartPeriod={organizerChartPeriod}
                          onToggle={() => toggleProfileAccordion("statistics")}
                          onPeriodChange={setOrganizerStatsPeriod}
                          onDateFromChange={setOrganizerStatsDateFrom}
                          onDateToChange={setOrganizerStatsDateTo}
                          onChartPeriodChange={setOrganizerChartPeriod}
                        />
                      )}

                      {user.role === "organizer" && (
                        <ProfileOrganizerRankingTab
                          open={openProfileAccordions.organizerRanking}
                          onToggle={() => toggleProfileAccordion("organizerRanking")}
                          fields={ORGANIZER_RANKING_FIELDS}
                          settings={organizerRankingSettings}
                          draft={organizerRankingDraft}
                          mode={organizerRankingModeDraft}
                          modeDescriptions={organizerRankingModeDescriptions}
                          loading={organizerRankingLoading}
                          saving={organizerRankingSaving}
                          error={organizerRankingError}
                          success={organizerRankingSuccess}
                          onModeChange={(mode) => void handleOrganizerRankingModeChange(mode)}
                          onDuplicateOfficial={duplicateOrganizerRankingFromOfficial}
                          onUpdatePoint={updateOrganizerRankingPoint}
                          onRestoreDefault={() => void restoreOrganizerRankingDefault()}
                          onSave={() => void saveOrganizerRanking()}
                        />
                      )}
                      {user.role === "organizer" && (
                        <ProfileVisualIdentityTab
                          open={openProfileAccordions.visualIdentity}
                          onToggle={() => toggleProfileAccordion("visualIdentity")}
                          logoInputRef={organizerLogoInputRef}
                          publicNameDraft={visualIdentityPublicNameDraft}
                          logoDraft={visualIdentityLogoDraft}
                          mode={visualIdentityModeDraft}
                          modeDescriptions={visualIdentityModeDescriptions}
                          colorsDraft={visualIdentityColorsDraft}
                          backgroundMode={visualIdentityBackgroundDraft}
                          previewStyle={visualIdentityPreviewStyle}
                          hasLowContrast={visualIdentityHasLowContrast}
                          loading={visualIdentityLoading}
                          saving={visualIdentitySaving}
                          error={visualIdentityError}
                          onLogoFile={handleOrganizerLogoFile}
                          onPublicNameChange={(value) => {
                            setVisualIdentityPublicNameDraft(value);
                            setVisualIdentityError("");
                          }}
                          onRemoveLogo={removeOrganizerLogo}
                          onModeChange={handleVisualIdentityModeChange}
                          onColorChange={updateVisualIdentityColor}
                          onBackgroundModeChange={(backgroundMode) => {
                            setVisualIdentityBackgroundDraft(backgroundMode);
                            setVisualIdentityError("");
                          }}
                          onSave={() => void saveVisualIdentity()}
                        />
                      )}
                    </>
                  )}
                  <ProfileCategoryRequestTab
                    user={user}
                    dashboardTexts={dashboardTexts}
                    info={info}
                    open={
                      showCategoryRequestForm ||
                      openProfileAccordions.categoryRequest
                    }
                    requestedCategoryDraft={requestedCategoryDraft}
                    categoryRequestReasonDraft={categoryRequestReasonDraft}
                    categoryRequestObservationDraft={
                      categoryRequestObservationDraft
                    }
                    pendingCategoryRequest={pendingCategoryRequest}
                    categoryRequestLoading={categoryRequestLoading}
                    categoryRequestSaving={categoryRequestSaving}
                    categoryRequestError={categoryRequestError}
                    categoryRequestSuccess={categoryRequestSuccess}
                    categoryChangeRequests={categoryChangeRequests}
                    sortedCategoryChangeRequests={
                      sortedCategoryChangeRequests
                    }
                    onToggle={() => {
                      if (showCategoryRequestForm) {
                        setShowCategoryRequestForm(false);
                        return;
                      }
                      toggleProfileAccordion("categoryRequest");
                    }}
                    onRequestedCategoryChange={setRequestedCategoryDraft}
                    onReasonChange={setCategoryRequestReasonDraft}
                    onObservationChange={setCategoryRequestObservationDraft}
                    onSubmit={() => void onRequestCategoryChange()}
                    onCancel={() => {
                      setShowCategoryRequestForm(false);
                      setCategoryRequestError("");
                      setCategoryRequestReasonDraft("");
                      setCategoryRequestObservationDraft("");
                      setRequestedCategoryDraft("");
                    }}
                    localizedCategoryLabel={localizedCategoryLabel}
                  />

                  {!showCategoryRequestForm && (
                    <ProfileSecurityTab
                      user={user}
                      open={openProfileAccordions.security}
                      onToggle={() => toggleProfileAccordion("security")}
                      securityEmailDraft={securityEmailDraft}
                      onSecurityEmailDraftChange={setSecurityEmailDraft}
                      onResetSecurityEmail={() => {
                        setEmailError("");
                        setSecurityEmailDraft(user.email || "");
                      }}
                      onSaveSecurityEmail={onSaveSecurityEmail}
                      hasEmailChange={hasEmailChange}
                      emailSaving={emailSaving}
                      emailLinkLoading={emailLinkLoading}
                      emailError={emailError}
                      onRequestEmailConfirmationLink={
                        onRequestEmailConfirmationLink
                      }
                      currentPasswordDraft={currentPasswordDraft}
                      onCurrentPasswordDraftChange={setCurrentPasswordDraft}
                      newPasswordDraft={newPasswordDraft}
                      onNewPasswordDraftChange={setNewPasswordDraft}
                      newPasswordConfirmDraft={newPasswordConfirmDraft}
                      onNewPasswordConfirmDraftChange={
                        setNewPasswordConfirmDraft
                      }
                      onChangePassword={onChangePassword}
                      passwordSaving={passwordSaving}
                      passwordError={passwordError}
                      passwordSuccess={passwordSuccess}
                    />
                  )}
                  {tab === "profile" && !showCategoryRequestForm && (
                    <ProfileEventsTab
                      open={openProfileAccordions.history}
                      onToggle={() => toggleProfileAccordion("history")}
                      nameFilter={nameFilter}
                      onNameFilterChange={setNameFilter}
                      statusFilter={eventStatusFilter}
                      onStatusFilterChange={setEventStatusFilter}
                      typeFilter={eventTypeFilter}
                      onTypeFilterChange={setEventTypeFilter}
                      stats={profileEventStats}
                      loading={challengeLoading}
                      events={paginatedChallenges}
                      page={normalizedEventsPage}
                      totalPages={eventsTotalPages}
                      onPageChange={setEventsPage}
                      onCopySuccess={showSuccessToast}
                      onCopyError={showErrorToast}
                    />
                  )}
                  {tab === "profile" && !showCategoryRequestForm && (
                    <ProfileMyGamesTab
                      open={openProfileAccordions.myGames}
                      onToggle={() => toggleProfileAccordion("myGames")}
                      upcomingGames={myGamesUpcoming}
                      historyGames={myGamesHistory}
                      loading={myGamesLoading}
                      error={myGamesError}
                    />
                  )}
                  {tab === "profile" && !showCategoryRequestForm && (
                    <ProfileOpenMatchesTab
                      open={openProfileAccordions.openMatches}
                      onToggle={() => toggleProfileAccordion("openMatches")}
                      groups={myOpenMatchGroups}
                      total={myOpenMatchesTotal}
                      loading={myOpenMatchesLoading}
                      error={myOpenMatchesError}
                      userId={user.id}
                      onEditMatch={setEditingOpenMatch}
                      onReviewParticipant={onReviewOpenMatchParticipant}
                      reviewingParticipantId={openMatchParticipantActionId}
                    />
                  )}
                  {tab === "profile" && !showCategoryRequestForm && (
                    <ProfileRegistrationsTab
                      open={openProfileAccordions.registrations}
                      onToggle={() => toggleProfileAccordion("registrations")}
                      loading={myRegistrationsLoading}
                      items={registrationItems}
                      filteredItems={filteredRegistrations}
                      statusFilter={registrationStatusFilter}
                      onStatusFilterChange={setRegistrationStatusFilter}
                      statusSummary={registrationStatusSummary}
                      runningActionId={registrationActionId}
                      onCancelRegistration={(id) => void onCancelRegistration(id)}
                      onDeleteRegistration={(id) => void onDeleteRegistration(id)}
                      gameTournamentIds={myGamesTournamentIds}
                      onViewGames={openMyGamesModalForTournament}
                    />
                  )}
                </div>
              )}
            </section>
          </div>

          {toast && (
            <Toast
              message={toast.message}
              variant={toast.variant}
              onClose={() => setToast(null)}
            />
          )}

          <ProfileShareModal
            open={Boolean(user && profileShareOpen)}
            preview={profileSharePreview}
            loading={profileShareLoading}
            status={profileShareStatus}
            dashboardTexts={dashboardTexts}
            info={info}
            onClose={() => setProfileShareOpen(false)}
            onShare={() => void shareProfile()}
            onDownload={downloadProfileShare}
            onCopyLink={() => void copyProfileShareLink()}
          />
          {myGamesModalTournamentId && (
            <div
              className="profile-my-games-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label={siteTexts.profile.myGames.title}
            >
              <button
                type="button"
                className="profile-my-games-modal__scrim"
                aria-label={siteTexts.profile.myGames.closeModal}
                onClick={() => setMyGamesModalTournamentId(null)}
              />
              <section className="profile-my-games-modal">
                <header className="profile-my-games-modal__header">
                  <div>
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      sports_tennis
                    </span>
                    <div>
                      <h3>
                        {selectedModalTournamentName ||
                          siteTexts.profile.myGames.title}
                      </h3>
                      <p>{siteTexts.profile.myGames.modalSubtitle}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="profile-my-games-modal__close"
                    aria-label={siteTexts.profile.myGames.closeModal}
                    onClick={() => setMyGamesModalTournamentId(null)}
                  >
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      close
                    </span>
                  </button>
                </header>
                <ProfileMyGamesContent
                  upcomingGames={selectedModalUpcomingGames}
                  historyGames={selectedModalHistoryGames}
                  loading={myGamesLoading}
                  error={myGamesError}
                />
              </section>
            </div>
          )}
          <MatchCreatePanel
            open={Boolean(editingOpenMatch)}
            clubs={openMatchClubs}
            loadingClubs={openMatchClubsLoading}
            mode="edit"
            initialMatch={editingOpenMatch}
            defaultOrganizerName={user?.name || ""}
            onSubmit={onUpdateOpenMatch}
            onClose={() => setEditingOpenMatch(null)}
          />
        </>
      )}
    </AppLayout>
  );
}
